import { CoreTypography } from '@youscience/core';
import { RouteObject } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import {
  organizationDetailsViewLoaderType,
  organizationsDetailsLoaderType,
} from '@features/organizations/interfaces/orgLoaderTypes';
import { ecpOrganizationsLoaderType } from '@features/ecp/interfaces/ecpLoaderTypes';
import { discoveryOrganizationsLoaderType } from '@features/discovery/interfaces/discoveryLoaderTypes';
import ErrorPage from '../error-page';
import { DISCOVERY_ROUTES, ECP_ROUTES, ORGANIZATION_ROUTES } from '.';
import { ECP_SETTINGS_ENABLED, SUNSET_ECP_ENABLED } from '../constants/env';
import { ecpRoutes } from './ecpRoutes';
import { ccrRoutes } from './ccrRoutes';
import { CommonBreadcrumbs, commonTenantBreadCrumbs } from './commonBreadcrumbs';
// Lazy components

const queryClient = new QueryClient();

// The Education Connections settings page is served by ECP code, so it is removed
// along with the rest of the ECP routes when the sunset flag is enabled.
const educationConnectionsRoutes: RouteObject[] = SUNSET_ECP_ENABLED
  ? []
  : [
      {
        async lazy() {
          const { EducationConnectionsSettings } =
            await import('@features/ecp/EducationConnectionsSettings/EducationConnectionsSettings');

          if (!ECP_SETTINGS_ENABLED) {
            window.location.replace(ORGANIZATION_ROUTES.ORGANIZATIONS);
          }

          return { Component: ECP_SETTINGS_ENABLED ? EducationConnectionsSettings : ErrorPage };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { ecpSettingsLoader } = await import('@features/ecp/loaders');

          return ecpSettingsLoader({ request, params });
        },
        path: ECP_ROUTES.EDUCATION_CONNECTIONS_SETTINGS,
        handle: {
          crumb: ({ tenant }: ecpOrganizationsLoaderType) => {
            return [
              ...commonTenantBreadCrumbs(tenant?.tenantId, tenant?.name),
              <CoreTypography color='text.secondary'>Education Connections</CoreTypography>,
            ];
          },
        },
      },
    ];

export const organizationRoutes: RouteObject[] = [
  {
    path: ORGANIZATION_ROUTES.ORGANIZATIONS,
    errorElement: <ErrorPage />,
    children: [
      {
        async lazy() {
          const { OrganizationsTable } = await import('@features/organizations/OrganizationsTable/OrganizationsTable');

          return { Component: OrganizationsTable };
        },
        errorElement: <ErrorPage />,
        index: true,
        async loader() {
          const { organizationsTableLoader } = await import('@features/organizations/loaders');

          return organizationsTableLoader(queryClient);
        },
        handle: {
          crumb: () => [
            <CommonBreadcrumbs.Home />,
            <CoreTypography color='text.secondary'>Organizations</CoreTypography>,
          ],
        },
      },
      {
        async lazy() {
          const { OrganizationsView } = await import('@features/organizations/OrganizationsView/OrganizationsView');

          return { Component: OrganizationsView };
        },
        async loader({ request, params }) {
          const { organizationDetailsLoader } = await import('@features/organizations/loaders');

          return organizationDetailsLoader({ request, params });
        },
        path: ORGANIZATION_ROUTES.DETAILS,
        handle: {
          crumb: (data: organizationDetailsViewLoaderType) => {
            if (!data?.details) return [];

            return [
              <CommonBreadcrumbs.Home />,
              <CommonBreadcrumbs.Organizations />,
              <CoreTypography color='text.secondary'>{data?.details?.name || 'Details'}</CoreTypography>,
            ];
          },
        },
      },
      {
        async lazy() {
          const { OrganizationEdit } = await import('@features/organizations/OrganizationEdit/OrganizationEdit');

          return { Component: OrganizationEdit };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const newParams = { ...params, ...{ isNew: 'yes' } };
          const { organizationDetailsLoader } = await import('@features/organizations/loaders');

          return organizationDetailsLoader({ request, params: newParams });
        },
        path: ORGANIZATION_ROUTES.ADD_ORG,
        handle: {
          crumb: () => {
            return [
              <CommonBreadcrumbs.Home />,
              <CommonBreadcrumbs.Organizations />,
              <CoreTypography color='text.secondary'>Add Organization</CoreTypography>,
            ];
          },
        },
      },
      {
        async lazy() {
          const { OrganizationEdit } = await import('@features/organizations/OrganizationEdit/OrganizationEdit');

          return { Component: OrganizationEdit };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { organizationDetailsLoader } = await import('@features/organizations/loaders');

          return organizationDetailsLoader({ request, params });
        },
        path: ORGANIZATION_ROUTES.EDIT_TENANT,
        handle: {
          crumb: ({ details }: organizationsDetailsLoaderType) => {
            return [
              ...commonTenantBreadCrumbs(details?.tenantId, details?.name),
              <CoreTypography color='text.secondary'>Edit</CoreTypography>,
            ];
          },
        },
      },
      {
        async lazy() {
          const { RefactoredBulkInvite } =
            await import('@features/invitations/RefactoredBulkInvite/RefactoredBulkInvite');

          return { Component: RefactoredBulkInvite };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { organizationDetailsLoader } = await import('@features/organizations/loaders');
          const { orgListLoader } = await import('@features/invitations/loaders');

          await orgListLoader();
          return organizationDetailsLoader({ request, params });
        },
        path: ORGANIZATION_ROUTES.BULK_INVITE_WITH_ID,
        handle: {
          crumb: ({ details }: organizationsDetailsLoaderType) => [
            ...commonTenantBreadCrumbs(details?.tenantId, details?.name),
            <CoreTypography color='text.secondary'>Bulk Invite</CoreTypography>,
          ],
        },
      },
      {
        async lazy() {
          const { RefactoredBulkInvite } =
            await import('@features/invitations/RefactoredBulkInvite/RefactoredBulkInvite');

          return { Component: RefactoredBulkInvite };
        },
        errorElement: <ErrorPage />,
        async loader() {
          const { orgListLoader } = await import('@features/invitations/loaders');

          return orgListLoader();
        },
        path: ORGANIZATION_ROUTES.BULK_INVITE_WITHOUT_ID,
        handle: {
          crumb: () => [
            <CommonBreadcrumbs.Home />,
            <CommonBreadcrumbs.Organizations />,
            <CoreTypography color='text.secondary'>Bulk Invite</CoreTypography>,
          ],
        },
      },
      {
        async lazy() {
          const { FamilyContactsBulkInvite } = await import('@features/invitations/FamilyContactsBulkInvite');

          return { Component: FamilyContactsBulkInvite };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { organizationDetailsLoader } = await import('@features/organizations/loaders');

          return organizationDetailsLoader({ request, params });
        },
        path: ORGANIZATION_ROUTES.FAMILY_CONTACTS_BULK_INVITE,
        handle: {
          crumb: ({ details }: organizationsDetailsLoaderType) => [
            ...commonTenantBreadCrumbs(details?.tenantId, details?.name),
            <CoreTypography color='text.secondary'>Family Contacts Bulk Invite</CoreTypography>,
          ],
        },
      },
      {
        async lazy() {
          const { UsersBulkInvite } = await import('@features/invitations/UsersBulkInvite');

          return { Component: UsersBulkInvite };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { organizationDetailsLoader } = await import('@features/organizations/loaders');

          return organizationDetailsLoader({ request, params });
        },
        path: ORGANIZATION_ROUTES.USERS_BULK_INVITE,
        handle: {
          crumb: ({ details }: organizationsDetailsLoaderType) => [
            ...commonTenantBreadCrumbs(details?.tenantId, details?.name),
            <CoreTypography color='text.secondary'>Users Bulk Invite</CoreTypography>,
          ],
        },
      },
      {
        path: `${ORGANIZATION_ROUTES.DETAILS}/${ORGANIZATION_ROUTES.ADD_INTEGRATION}`,
        async lazy() {
          const { AddIntegration } =
            await import('@features/organizations/OrganizationsView/Integrations/AddIntegrations/AddIntegrations');

          return { Component: AddIntegration };
        },
        errorElement: <ErrorPage />,
      },
      ...educationConnectionsRoutes,
      {
        async lazy() {
          const { DiscoverySettings } = await import('@features/discovery/DiscoverySettings/DiscoverySettings');

          return { Component: DiscoverySettings };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { discoverySettingsLoader } = await import('@features/discovery/loaders');

          return discoverySettingsLoader({ request, params });
        },
        path: DISCOVERY_ROUTES.DISCOVERY_SETTINGS,
        handle: {
          crumb: ({ tenant }: discoveryOrganizationsLoaderType) => {
            return [
              ...commonTenantBreadCrumbs(tenant?.tenantId, tenant?.name),
              <CoreTypography color='text.secondary'>Discovery</CoreTypography>,
            ];
          },
        },
      },
      ...ecpRoutes,
      ...ccrRoutes,
    ],
  },
];
